import styles from './MetricTiles.module.scss'

import type { SummaryQualityStats } from '@/api/generated/api'
import type { SummaryFeedbackStats } from '@/api/meetingSummaryFeedback'
import { percent } from '@/utils'

const EMPTY = '—'

const seconds = (ms: number | null) => (ms === null ? EMPTY : `${(ms / 1000).toFixed(1)} s`)

// Under $0.10, two significant digits keep cheap models apart ($0.0004 vs $0.0014) instead of rounding to $0.000.
const usd = (value: number) =>
  new Intl.NumberFormat(
    'en-US',
    value > 0 && value < 0.1
      ? { style: 'currency', currency: 'USD', maximumSignificantDigits: 2 }
      : { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }
  ).format(value)

type TileValue = { value: string; detail?: string }

const Tile = ({ label, isBusy, value, detail }: TileValue & { label: string; isBusy: boolean }) => (
  <div className={styles.tile} aria-busy={isBusy}>
    <span>{label}</span>
    <strong>{isBusy ? EMPTY : value}</strong>
    {!isBusy && detail && <small>{detail}</small>}
  </div>
)

const coverage = (meetings: number, runs: number): TileValue => ({
  value: `${meetings.toLocaleString()} / ${runs.toLocaleString()}`,
  detail: runs ? 'Each counted by its own date in this range' : 'No summary runs in this range',
})

const latency = ({ overall }: SummaryQualityStats['latency']): TileValue =>
  overall.measured === 0
    ? { value: EMPTY, detail: 'Not measured in this range' }
    : { value: seconds(overall.p50), detail: `P95 ${seconds(overall.p95)} · P99 ${seconds(overall.p99)}` }

const cost = ({ usdPerSummary, overallUsd, unpricedRuns }: SummaryQualityStats['cost']): TileValue => {
  const runsText = `${unpricedRuns.toLocaleString()} unpriced ${unpricedRuns === 1 ? 'run' : 'runs'}`
  if (usdPerSummary === null)
    return { value: EMPTY, detail: unpricedRuns > 0 ? `${runsText} on models with no price` : undefined }
  const unpriced = unpricedRuns > 0 ? ` · excludes ${runsText}` : ''
  return { value: usd(usdPerSummary), detail: `${usd(overallUsd)} total · estimated${unpriced}` }
}

type Props = {
  counts: SummaryFeedbackStats['overall'] | undefined
  quality: SummaryQualityStats | undefined
  isCountsLoading: boolean
  isQualityLoading: boolean
}

const MetricTiles = ({ counts, quality, isCountsLoading, isQualityLoading }: Props) => {
  const missing = { value: EMPTY }
  return (
    <section className={styles.tiles} aria-label="Summary quality metrics">
      <Tile
        label="👍 rate"
        isBusy={isCountsLoading}
        {...(counts
          ? {
              value: percent(counts.pctUp),
              detail: `${counts.up.toLocaleString()} 👍 · ${counts.down.toLocaleString()} 👎 · ${counts.total.toLocaleString()} ratings`,
            }
          : missing)}
      />
      <Tile
        label="Meetings with feedback / summary runs"
        isBusy={isCountsLoading || isQualityLoading}
        {...(counts && quality ? coverage(counts.meetings, quality.summaries.overall) : missing)}
      />
      <Tile label="Latency P50" isBusy={isQualityLoading} {...(quality ? latency(quality.latency) : missing)} />
      <Tile label="Cost per summary" isBusy={isQualityLoading} {...(quality ? cost(quality.cost) : missing)} />
      <Tile label="LLM naming rate" isBusy={false} value={EMPTY} detail="No data yet" />
    </section>
  )
}

export default MetricTiles
